// src/pages/Signup.jsx
import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext.jsx";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import StepBar from "../components/StepBar.jsx";
import SEO from "../components/SEO.jsx";
import { seoPresets } from "../seoPresets";

export default function Signup() {
  const seo = seoPresets["/signup"];
  const { signupInit, googleLogin } = useAuth();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resentMsg, setResentMsg] = useState("");

  const nav = useNavigate();
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const redirect = params.get("redirect") || "/pricing?locked=1";
  const inviteCode = params.get("invite") || params.get("ref") || "";
  const passwordNext =
    `/set-password?next=${encodeURIComponent(redirect)}` +
    (inviteCode ? `&invite=${encodeURIComponent(inviteCode)}` : "");

  useEffect(() => {
    const emailFromUrl = params.get("email");
    if (emailFromUrl) {
      setEmail(emailFromUrl);
    }
  }, [loc.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentStep = sent ? 1 : 0;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError("");
    setResentMsg("");
    setLoading(true);
    try {
      await signupInit(email.trim());
      setSent(true);
    } catch (e2) {
      setError(e2?.message || "signup_init_failed");
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (!email || loading) return;
    setError("");
    setResentMsg("");
    setLoading(true);
    try {
      await signupInit(email.trim());
      setResentMsg("Email sent again.");
    } catch (e2) {
      setError(e2?.message || "resend_failed");
    } finally {
      setLoading(false);
    }
  };

  const onGoogleSuccess = async (cred) => {
    try {
      setError("");
      const res = await googleLogin(cred.credential);
      if (res?.needs_profile) {
        // Make Google signups also go through password step
        nav(passwordNext);
      } else {
        nav(redirect);
      }
    } catch (e) {
      setError(e?.message || "Google sign-in failed");
    }
  };

  const goEnterCode = () => {
    if (!email) return;
    // After code verification, go to /set-password, then redirect
    nav(
      `/verify-code?email=${encodeURIComponent(
        email
      )}&next=${encodeURIComponent(passwordNext)}`
    );
  };

  const useAnotherEmail = () => {
    setSent(false);
    setResentMsg("");
    setError("");
    setTimeout(() => document.getElementById("email")?.focus(), 0);
  };

  return (
    <>
      <SEO
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
        image={seo.openGraph?.image}
        jsonLd={seo.jsonLd}
        ogType={seo.openGraph?.type || "website"}
        twitterCard={seo.twitter?.card || "summary_large_image"}
        robots="noindex, nofollow"
      />
      <div className="auth-wrap">
        <style>{css}</style>

        <div className="auth-grid">
          {/* LEFT: sign up */}
          <section className="auth-main">
            <header className="auth-header">
              <Link to="/" className="brand-link">
                <div className="logo">
                  <img
                    className="brand-icon"
                    src="https://imgur.com/ef4tlNy.png"
                    alt=""
                    aria-hidden
                  />
                </div>
                <div className="brand-names">
                  <span className="brand-top">MusicBase</span>
                  <span className="brand-sub">playlist outreach</span>
                </div>
              </Link>

              <div className="auth-switch">
                <button
                  type="button"
                  className="auth-switch-btn"
                  onClick={() => nav("/login")}
                >
                  Log in
                </button>
                <button type="button" className="auth-switch-btn active">
                  Sign up
                </button>
              </div>
            </header>

            <div className="auth-body">
              {/* STEP BAR ROW (TOP) */}
              <div className="auth-stephead">
                <div className="auth-stepbar">
                  <StepBar
                    steps={["Email", "Verify", "Password"]}
                    current={currentStep}
                  />
                </div>

                <div className="auth-copy">
                  <h1 className="auth-title">
                    {sent ? "Check your inbox" : "Create your account"}
                  </h1>
                  {!sent ? (
                    <p className="auth-subtitle">
                      Start with your email. We&apos;ll send a 6-digit code or
                      magic link to verify you before you set a password.
                    </p>
                  ) : (
                    <p className="auth-subtitle">
                      We sent a confirmation to{" "}
                      <strong>{email || "your email"}</strong>. Use the code or
                      link to finish verifying.
                    </p>
                  )}
                  {inviteCode && (
                    <p className="auth-subtitle" style={{ marginTop: 6 }}>
                      You&apos;re joining with an invite. When you finish signup,
                      you&apos;ll get <strong>+10 bonus credits</strong> and your
                      friend gets +10 as well.
                    </p>
                  )}

                </div>
              </div>

              {!sent ? (
                <>
                  <form onSubmit={onSubmit} className="form" noValidate>
                    <div className={"field" + (email ? " has-value" : "")}>
                      <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        autoComplete="email"
                        inputMode="email"
                        placeholder=" "
                      />
                      <label htmlFor="email">Email</label>
                    </div>

                    {error && (
                      <div className="error" role="alert">
                        {error}
                      </div>
                    )}

                    <button
                      className="submit"
                      type="submit"
                      disabled={loading || !email}
                    >
                      {loading ? "Sending…" : "Continue"}
                    </button>
                  </form>

                  <div className="auth-divider">
                    <span />
                    <span>or continue through</span>
                    <span />
                  </div>

                  {/* Google signup */}
                  <div className="auth-social">
                    <div className="auth-social-inner">
                      <GoogleOAuthProvider
                        clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}
                      >
                        <GoogleLogin
                          onSuccess={onGoogleSuccess}
                          onError={() =>
                            setError("Google sign-in failed")
                          }
                        />
                      </GoogleOAuthProvider>
                    </div>
                  </div>

                  <p className="auth-footnote">
                    By continuing, you agree to MusicBase&apos;s{" "}
                    <Link to="/terms">Terms</Link> and{" "}
                    <Link to="/privacy">Privacy Policy</Link>.
                  </p>
                </>
              ) : (
                <>
                  {error && (
                    <div className="error" role="alert">
                      {error}
                    </div>
                  )}

                  {resentMsg && (
                    <div className="info-pill" role="status">
                      {resentMsg}
                    </div>
                  )}

                  <div className="btn-row">
                    <button
                      className="submit"
                      type="button"
                      onClick={resend}
                      disabled={loading}
                    >
                      {loading ? "Resending…" : "Resend email"}
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={goEnterCode}
                    >
                      Enter code
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={useAnotherEmail}
                    >
                      Use another email
                    </button>
                  </div>

                  <p className="auth-footnote" style={{ marginTop: 14 }}>
                    Didn&apos;t get anything? Check your spam folder or make
                    sure the address above is correct.
                  </p>
                </>
              )}
            </div>
          </section>

          {/* RIGHT: campaign preview — same as login */}
          <aside className="auth-aside" aria-hidden="true">
            <div className="hero-shell">
              <div className="hero-pill">
                <span className="hero-dot" />
                <span>Campaign preview</span>
              </div>

              <h2 className="hero-title">
                See delivered and replies in one view.
              </h2>
              <p className="hero-text">
                As your campaigns run, MusicBase shows how many emails were
                delivered, bounced, or got a reply — the same metrics you see in
                your Campaigns table.
              </p>

              <div className="hero-card">
                <div className="hero-card-header">
                  <div className="hero-avatar">MB</div>
                  <div className="hero-card-text">
                    <div className="hero-card-title">Indie Pitch — Sample</div>
                    <div className="hero-card-sub">
                      Campaign overview · preview only
                    </div>
                  </div>
                </div>

                <div className="hero-rows">
                  <div className="hero-row">
                    <span className="hero-label">Delivered</span>
                    <span className="hero-value">54</span>
                  </div>
                  <div className="hero-row">
                    <span className="hero-label">Replied</span>
                    <span className="hero-value">29</span>
                  </div>
                  <div className="hero-row">
                    <span className="hero-label">Bounced</span>
                    <span className="hero-value">2</span>
                  </div>
                  <div className="hero-row">
                    <span className="hero-label">Spam</span>
                    <span className="hero-value">1</span>
                  </div>
                </div>
              </div>

              <div className="hero-metrics">
                <div>
                  <div className="hero-metric-number">Outreach stats</div>
                  <div className="hero-metric-label">
                    Delivered, bounced and replied counts for every campaign.
                  </div>
                </div>
                <div>
                  <div className="hero-metric-number">Your own inbox</div>
                  <div className="hero-metric-label">
                    Connect Gmail or Outlook so the data comes from your
                    mailbox, not guesses.
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}

const css = `
:root{
  --bg:#f5f4ff;
  --surface:#ffffff;
  --surface-soft:#f9fafb;
  --muted:#6b7280;
  --text:#0b0b0c;
  --accent:#7c5cff;
  --accent-2:#00d4ff;
  --ring:rgba(124,92,255,.25);
  --border:#e5e7eb;
  --shadow:0 24px 70px rgba(15,23,42,.14);
}

/* keep auth page non-scrollable / centered (same as login) */
html, body {
  min-height: 100%;
  overflow-x: hidden;
  overflow-y: hidden;
  scroll-behavior: smooth;
}

*{ box-sizing:border-box; }

.auth-wrap{
  min-height:100dvh;
  background:
    radial-gradient(1100px 650px at -10% -10%, rgba(124,92,255,.18), transparent 55%),
    radial-gradient(1100px 650px at 110% 0%, rgba(0,212,255,.18), transparent 55%),
    linear-gradient(#ffffff, #f9fafb);
  display:flex;
  align-items:center;
  justify-content:center;
  padding:32px 16px;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}

.auth-grid{
  max-width:1120px;
  background:var(--surface);
  border-radius:24px;
  box-shadow:var(--shadow);
  border:1px solid var(--border);
  display:grid;
  grid-template-columns:minmax(0,440px) minmax(0,1fr);
  overflow:hidden;
}

/* LEFT SIDE */
.auth-main{
  padding:28px 32px 26px;
  display:flex;
  flex-direction:column;
  background:linear-gradient(180deg,#ffffff,#f9fafb);
}

.auth-header{
  display:flex;
  justify-content:space-between;
  align-items:center;
  gap:16px;
  margin-bottom:24px;
}

.brand-link{
  display:flex;
  align-items:center;
  gap:12px;
  text-decoration:none;
  color:inherit;
}
.logo{
  width:40px; height:40px;
  border-radius:14px;
  display:grid; place-items:center;
  font-size:18px; font-weight:900; color:#fff;
}
.brand-names{
  display:flex;
  flex-direction:column;
  line-height:1.05;
  align-items:flex-start;
  text-align:left;
}
.brand-top{font-weight:800; letter-spacing:.03em; font-size:15px;}
.brand-sub{font-size:11px; text-transform:uppercase; letter-spacing:.16em; color:var(--muted);}

/* toggle */
.auth-switch{
  display:inline-flex;
  padding:4px;
  border-radius:999px;
  background:#f3f4ff;
  border:1px solid #e0e7ff;
}
.auth-switch-btn{
  border:none;
  background:transparent;
  color:#6b7280;
  padding:6px 14px;
  border-radius:999px;
  font-size:12px;
  font-weight:600;
  cursor:pointer;
}
.auth-switch-btn.active{
  background:#111827;
  color:#f9fafb;
}

/* body */
.auth-body{
  flex:1;
  display:flex;
  flex-direction:column;
}

/* signup-specific step header */
.auth-stephead{
  display:flex;
  flex-direction:column;
  align-items:center;
  gap:10px;
  margin-bottom:18px;
}
.auth-stepbar{
  width:100%;
  display:flex;
  justify-content:center;
}
.auth-stepbar > *{
  max-width:280px;
}
.auth-copy{
  max-width:280px;
  text-align:center;
}

/* titles */
.auth-title{
  margin:0 0 6px 0;
  font-size:30px;
  line-height:1.1;
  letter-spacing:-.02em;
  color:var(--text);
  text-align:center;
}
.auth-subtitle{
  margin:0 0 18px 0;
  font-size:14px;
  color:var(--muted);
  text-align:center;
}

/* Google */
.auth-social{
  margin-bottom:12px;
}
.auth-social-inner{
  border-radius:14px;
  padding:10px 10px;
  background:#ffffff;
  display:flex;
  justify-content:center;
}

/* divider */
.auth-divider{
  display:grid;
  grid-template-columns:1fr auto 1fr;
  align-items:center;
  gap:8px;
  margin:10px 0 14px;
}
.auth-divider span:nth-child(1),
.auth-divider span:nth-child(3){
  height:1px;
  background:#9ca3af;
}
.auth-divider span:nth-child(2){
  font-size:11px;
  font-weight:600;
  text-transform:uppercase;
  letter-spacing:.16em;
  color:#9ca3af;
}

/* fields */
.form{display:grid; gap:12px;}

.field{position:relative;}
.field input{
  width:100%;
  padding:16px 90px 10px 14px;
  border-radius:14px;
  border:1px solid var(--border);
  background:#ffffff;
  color:var(--text);
  font-size:14px;
  outline:none;
}
.field input::placeholder{color:transparent;}
.field label{
  position:absolute;
  left:14px;
  top:13px;
  pointer-events:none;
  color:var(--muted);
  background:#ffffff;
  padding:0 .25em;
  border-radius:999px;
  transition:all .16s ease;
  font-size:13px;
}
.field input:focus{
  box-shadow:0 0 0 5px var(--ring);
  border-color:#c4b5fd;
}
.field input:focus + label,
.field.has-value label{
  top:-8px;
  font-size:11px;
  color:#4b5563;
}

/* error + primary button */
.error{
  background:#fef2f2;
  border:1px solid #fecaca;
  color:#7f1d1d;
  padding:10px 12px;
  border-radius:12px;
  font-size:13px;
}

.submit{
  margin-top:4px;
  padding:13px 14px;
  border-radius:14px;
  font-weight:800;
  letter-spacing:.03em;
  border:none;
  cursor:pointer;
  background:linear-gradient(90deg,var(--nav-accent),var(--nav-accent-2));
  color:#fff;
  box-shadow:0 10px 40px rgba(124,92,255,.35);
}
.submit:disabled{
  opacity:.7;
  cursor:default;
  box-shadow:none;
}

/* secondary buttons + info pill for verification state */
.btn-row{
  display:flex;
  flex-wrap:wrap;
  gap:10px;
  margin-top:18px;
}
.btn-secondary{
  padding:13px 14px;
  border-radius:14px;
  font-weight:800;
  letter-spacing:.03em;
  border:1px solid var(--border);
  background:#fff;
  color:var(--accent);
  cursor:pointer;
}
.btn-secondary:disabled{
  opacity:.7;
  cursor:default;
}
.info-pill{
  margin-top:12px;
  background:#f3f6ff;
  border:1px solid #d9dfff;
  color:#111827;
  padding:10px 12px;
  border-radius:12px;
  font-size:13px;
}

.auth-footnote{
  margin:10px 0 0;
  font-size:11px;
  color:#9ca3af;
}
.auth-footnote a{
  color:#7c5cff;
  text-decoration:underline;
}

/* RIGHT SIDE */
.auth-aside{
  padding:32px 40px;
  background:linear-gradient(135deg,#eef2ff,#fdfbff);
  display:flex;
  align-items:center;
  justify-content:center;
}
.hero-shell{
  max-width:380px;
}
.hero-pill{
  display:inline-flex;
  align-items:center;
  gap:6px;
  padding:4px 10px;
  border-radius:999px;
  background:#111827;
  color:#e5e7eb;
  font-size:11px;
  font-weight:600;
  text-transform:uppercase;
  letter-spacing:.16em;
  margin-bottom:10px;
}
.hero-dot{
  width:7px; height:7px; border-radius:999px;
  background:linear-gradient(135deg,var(--accent),var(--accent-2));
}
.hero-title{
  margin:0 0 8px;
  font-size:24px;
  line-height:1.25;
  letter-spacing:-.02em;
  color:#020617;
}
.hero-text{
  margin:0 0 18px;
  font-size:14px;
  color:#4b5563;
}

/* screenshot-style card */
.hero-card{
  background:#ffffff;
  border-radius:18px;
  padding:16px 16px 14px;
  border:1px solid rgba(148,163,184,.55);
  box-shadow:0 22px 60px rgba(15,23,42,.16);
  margin-bottom:18px;
}
.hero-card-header{
  display:flex;
  align-items:center;
  gap:10px;
  margin-bottom:10px;
}
.hero-avatar{
  width:34px; height:34px;
  border-radius:12px;
  background:linear-gradient(90deg,var(--nav-accent),var(--nav-accent-2));
  display:grid; place-items:center;
  font-size:13px; font-weight:800; color:#fff;
}
.hero-card-text{
  display:flex;
  flex-direction:column;
}
.hero-card-title{
  font-size:14px;
  font-weight:600;
  color:#020617;
}
.hero-card-sub{
  font-size:12px;
  color:#6b7280;
}
.hero-rows{
  display:grid;
  gap:6px;
}
.hero-row{
  display:flex;
  align-items:center;
  gap:6px;
  font-size:12px;
}
.hero-label{
  width:72px;
  color:#6b7280;
}
.hero-value{
  color:#111827;
}

/* metrics */
.hero-metrics{
  display:flex;
  flex-wrap:wrap;
  gap:18px;
}
.hero-metric-number{
  font-size:18px;
  font-weight:800;
  color:#020617;
}
.hero-metric-label{
  font-size:12px;
  color:#4b5563;
  max-width:220px;
}

@media (max-width: 960px){
  .auth-grid{
    border-radius:0px;
  }
  body {
    overflow-y: auto;
  }
  .auth-grid{
    grid-template-columns:1fr;
    max-width:560px;
  }
  .auth-aside{
    padding:24px 24px 12px;
  }
  .auth-main{
    padding:24px 22px 24px;
  }
  .auth-copy{
    max-width:none;
  }
}

@media (max-width: 560px){
  .auth-wrap{padding:0px;}
  .auth-header{align-items:flex-start;}
  .auth-switch{align-self:flex-end;}
}
`;
